import React from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@/components/ui-compat'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle, faTrash } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { FolderContentStats } from '../utils/folder-stats'

interface Props {
  isOpen: boolean
  folder: { id: string; name: string } | null
  stats: FolderContentStats | null
  onConfirm: () => void
  onCancel: () => void
  loading?: boolean
}

const DeleteFolderModal: React.FC<Props> = ({
  isOpen,
  folder,
  stats,
  onConfirm,
  onCancel,
  loading,
}) => {
  const { t } = useTranslation()

  if (!folder) return null

  const hasContents = stats && (stats.subfolderCount > 0 || stats.documentCount > 0)

  return (
    <Modal isOpen={isOpen} toggle={onCancel} centered>
      <ModalHeader toggle={onCancel}>
        <Icon icon={faTrash as any} className="mr-2 text-destructive" />
        {t('DocControl_delete_folder_title')}
      </ModalHeader>
      <ModalBody>
        {hasContents ? (
          <>
            <div className="flex items-start mb-3">
              <Icon
                icon={faExclamationTriangle as any}
                className="text-[hsl(var(--warning))] mr-3 mt-1"
                style={{ fontSize: '1.5rem' }}
              />
              <p className="mb-0">
                {t('DocControl_delete_folder_warning', { name: folder.name })}
              </p>
            </div>
            <ul className="mb-0 pl-4">
              {stats!.subfolderCount > 0 && (
                <li>{t('DocControl_delete_folder_subfolders', { count: stats!.subfolderCount })}</li>
              )}
              {stats!.documentCount > 0 && (
                <li>{t('DocControl_delete_folder_documents', { count: stats!.documentCount })}</li>
              )}
            </ul>
          </>
        ) : (
          <p className="mb-0">
            {t('DocControl_folder_delete_confirm', { name: folder.name })}
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onCancel} disabled={loading}>
          {t('Common_cancel')}
        </Button>
        <Button color="danger" onClick={onConfirm} disabled={loading}>
          <Icon icon={faTrash as any} className="mr-1" />
          {t('Common_delete')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default DeleteFolderModal
